import {
	Portal,
	RewriteToolsMenu,
	RewriteToolsResult,
	TextInput,
	TextInputHandle,
	usePlainTextRewriteTools,
	ValidationError
} from "client/Components";
import { IInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { ensureNotNil } from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useDelayedEvent, useIsTouchDevice } from "client/Hooks";
import { useIntersection } from "client/Hooks/useIntersection";
import { InlineActionList } from "client/Features/Hub/Builder/Actions/InlineActionList";
import { ElementLayout } from "client/Features/Hub/Builder/Elements/ElementLayout";
import { RewriteToolEditorKind, useRewriteToolsService } from "client/Features/ChatAssistant/useRewriteToolsService";
import { hubItemKindToRewriteToolTargetKindMap } from "client/Features/Hub/common";
import { useIsAiAssistantAvailable } from "client/Features/ChatAssistant/useIsAiAssistantAvailable";
import { HubItemComposerDialog } from "./HubItemComposerDialog";
import styles from "./BuilderTitleInput.module.less";

export type BuilderTitleInputProps = {
	attachable: IInputAttachable<string>;
	hubItemKind: HubItemKind;
	shouldFocusTitle: boolean;
	onFocus?: () => void;
};

export const BuilderTitleInput = (props: BuilderTitleInputProps): ReactElement => {
	const {
		attachable,
		hubItemKind,
		shouldFocusTitle,
		onFocus
	} = props;

	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [isImproveTextMenuOpen, setIsImproveTextMenuOpen] = useState<boolean>(false);
	const [selectionRange, setSelectionRange] = useState<[number, number]>([0, 0]);

	const isAiAssistantAvailable: boolean = useIsAiAssistantAvailable();
	const isTouchDevice: boolean = useIsTouchDevice();

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const inputRef: RefObject<TextInputHandle | null> = useRef<TextInputHandle>(null);
	const improveTextButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const isInViewPort: boolean = useIntersection(rootRef)
		?.isIntersecting ?? true;

	const {
		reducer
	} = attachable;

	const value: string = reducer.value ?? "";

	// We need a delay because clicking the improve text button
	// fires blur before the button receives focus
	const [blurHandler, cancelBlurHandler] = useDelayedEvent(
		(): void => {
			setIsFocused(false);
			setIsImproveTextMenuOpen(false);
		},
		0
	);

	const changeValueHandler = useCallback(
		(newValue: string): void => {
			reducer.setValue(newValue);
		},
		[reducer]
	);

	const focusInputHandler = useCallback(
		(): void => {
			ensureNotNil(inputRef.current)
				.focus();
		},
		[]
	);

	const closeImproveTextMenuHandler = useCallback(
		(): void => {
			setIsImproveTextMenuOpen(false);
		},
		[]
	);

	const focusHandler = useCallback(
		(): void => {
			cancelBlurHandler();
			setIsFocused(true);
			onFocus?.();
		},
		[cancelBlurHandler, onFocus]
	);

	const improveTextResult: RewriteToolsResult<string, string> = usePlainTextRewriteTools(
		value,
		changeValueHandler,
		focusInputHandler,
		{
			kind: "specific-range",
			from: selectionRange[0],
			to: selectionRange[1],
			shouldUseEntireContentWhenEmpty: true
		},
		useRewriteToolsService(
			hubItemKindToRewriteToolTargetKindMap[hubItemKind],
			RewriteToolEditorKind.PlainText
		)
	);

	return (
		<div
			ref={rootRef}
			className={styles.root}
			onFocus={focusHandler}
			onBlur={blurHandler}
		>
			{isFocused
				&& isAiAssistantAvailable
				&& !isTouchDevice
				&& improveTextResult.canRewrite
				&& isInViewPort
				&& (
					<TitleActionsOverlay anchorRef={rootRef}>
						<InlineActionList trackingId="title-actions">
							<ElementLayout.ImproveTextMenuItem
								onClick={() => setIsImproveTextMenuOpen(true)}
								ref={improveTextButtonRef}
							/>
						</InlineActionList>
						{isImproveTextMenuOpen && (
							<RewriteToolsMenu
								onClose={closeImproveTextMenuHandler}
								target={ensureNotNil(improveTextButtonRef.current)}
								trackingId="improve-text__drop-over"
								handlers={improveTextResult.handlers}
							/>
						)}
					</TitleActionsOverlay>
				)
			}
			<HubItemComposerDialog.BuilderTitle
				hasError={!attachable.validation.isValid()}
			>
				<TextInput
					{...attachable.attachToTextInput()}
					ref={inputRef}
					placeholder={localize("_EnterTitle")}
					isBorderless
					hasNoPadding
					onSelectionChange={(from, to) => setSelectionRange([from, to])}
					autoFocus={
						shouldFocusTitle
							? "focus"
							: undefined
					}
				/>
			</HubItemComposerDialog.BuilderTitle>
			<ValidationError {...attachable.attachToValidationError()} />
		</div>
	);
};
BuilderTitleInput.displayName = "BuilderTitleInput";

type OverlayPosition = {
	left: number;
	top: number;
};

type TitleActionsOverlayProps = {
	anchorRef: RefObject<HTMLElement | null>;
};

const TitleActionsOverlay = (props: PropsWithChildren<TitleActionsOverlayProps>): ReactElement | null => {
	const {
		anchorRef,
		children
	} = props;

	const [position, setPosition] = useState<OverlayPosition | null>(null);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			const anchorElement: HTMLElement | null = anchorRef.current;

			if (!anchorElement) {
				return undefined;
			}

			const updatePosition = (): void => {
				const rect: DOMRect = anchorElement.getBoundingClientRect();

				setPosition({
					top: rect.top,
					left: rect.left
				});
			};

			updatePosition();

			const abortController: AbortController = new AbortController();
			const signal: AbortSignal = abortController.signal;

			// The dialog slides in with a CSS transform animation, so the anchor's
			// initial rect is read mid-animation (off screen). Reposition once the
			// animation on an ancestor (the dialog) finishes so the overlay lands
			// in the right place.
			const repositionAfterAnimation = (args: AnimationEvent): void => {
				if (args.target instanceof Node && args.target.contains(anchorElement)) {
					updatePosition();
				}
			};

			document.addEventListener("animationend", repositionAfterAnimation, { signal });

			// Capture phase so scrolling of any ancestor container repositions the overlay.
			window.addEventListener("scroll", updatePosition, { capture: true, signal });
			window.addEventListener("resize", updatePosition, { signal });

			return (): void => {
				abortController.abort();
			};
		},
		[anchorRef]
	);

	if (!position) {
		return null;
	}

	return (
		<Portal container={document.body}>
			<div
				className={styles.actionsOverlay}
				style={{
					left: position.left,
					top: position.top
				}}
			>
				{children}
			</div>
		</Portal>
	);
};
TitleActionsOverlay.displayName = "TitleActionsOverlay";

